import { ActionLoader } from '@/common/ui/LoadingSpinners';
import { ArrowLeftIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface EditorHeaderProps {
  name: string;
  onNameChange: (name: string) => void;
  isSaving?: boolean;
  lastSaved?: number;
  backPath: string;
  saveDisabledReason?: string;
  actions?: React.ReactNode;
}

export default function EditorHeader({
  name,
  onNameChange,
  isSaving,
  lastSaved,
  backPath,
  saveDisabledReason,
  actions,
}: EditorHeaderProps) {
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [updatedName, setUpdatedName] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  const isViewOnly = !!saveDisabledReason;

  useEffect(() => {
    setUpdatedName(name);
  }, [name]);

  function startEditing() {
    if (isViewOnly) return;
    setIsEditingName(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function finishEditing() {
    setIsEditingName(false);
    onNameChange(updatedName);
  }

  return (
    <header className="flex items-center justify-between p-4 border-b bg-white">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(backPath)}>
          <ArrowLeftIcon size={20} />
        </Button>

        {isEditingName ? (
          <Input
            ref={inputRef}
            value={updatedName}
            onChange={(e) => setUpdatedName(e.target.value)}
            onBlur={finishEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finishEditing();
            }}
            className="w-64 font-medium"
          />
        ) : (
          <button
            id="custom-level-title"
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors"
            onClick={startEditing}
            disabled={isViewOnly}
          >
            {updatedName}
            {!isViewOnly && (
              <PencilSimpleIcon size={14} className="text-gray-400" />
            )}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {isSaving ? (
            <span className="flex items-center gap-1">
              <ActionLoader />
              Saving...
            </span>
          ) : lastSaved ? (
            `Last saved ${dayjs(lastSaved).fromNow()}`
          ) : (
            ''
          )}
        </span>
        {actions}
      </div>
    </header>
  );
}
